// ChatPage.jsx — shared Teacher<->Parent messaging UI, instantiated once per
// role via createChatPage(). No WebSockets anywhere in this app, so this
// polls: every 5s for new messages while a thread is open, same spirit as
// the existing NotificationBell.jsx's 60s poll (just faster, since a chat
// needs to feel responsive while actually open).
import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ChatBubbleLeftRightIcon, PaperAirplaneIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const css = (accent) => `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .cp2 * { box-sizing:border-box; }
  .cp2 { font-family:'Sora',sans-serif; color:#1a1d2e; height:calc(100vh - 140px); min-height:520px; display:flex; flex-direction:column; animation:cp2Fade .4s ease both; }
  @keyframes cp2Fade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .cp2-header { margin-bottom:1.2rem; }
  .cp2-title  { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .cp2-sub    { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .cp2-body { flex:1; min-height:0; display:grid; grid-template-columns:300px 1fr; background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  @media (max-width:760px) { .cp2-body { grid-template-columns:1fr; } .cp2-list.hide-mobile { display:none; } .cp2-thread.hide-mobile { display:none; } }
  .cp2-list { border-right:1px solid #edf0f9; display:flex; flex-direction:column; min-height:0; }
  .cp2-search { padding:12px; border-bottom:1px solid #edf0f9; }
  .cp2-search-box { display:flex; align-items:center; gap:6px; background:#f8f9fc; border:1.5px solid #e0e4f0; border-radius:9px; padding:7px 10px; }
  .cp2-search-box svg { width:15px; height:15px; color:#b0b5cc; flex-shrink:0; }
  .cp2-search-box input { border:none; background:none; outline:none; font-family:'Sora',sans-serif; font-size:.8rem; width:100%; }
  .cp2-contacts { flex:1; overflow-y:auto; }
  .cp2-contact { display:flex; align-items:center; gap:10px; padding:11px 14px; cursor:pointer; border-bottom:1px solid #f6f7fb; transition:background .12s; }
  .cp2-contact:hover { background:#f8f9fc; }
  .cp2-contact.active { background:${accent}14; }
  .cp2-avatar { width:36px; height:36px; border-radius:50%; background:${accent}; color:white; display:flex; align-items:center; justify-content:center; font-weight:700; font-size:.82rem; flex-shrink:0; }
  .cp2-contact-info { flex:1; min-width:0; }
  .cp2-contact-name { font-size:.83rem; font-weight:700; color:#0f1123; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .cp2-contact-sub { font-size:.7rem; color:#9094a8; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin-top:1px; }
  .cp2-unread { min-width:19px; height:19px; padding:0 5px; border-radius:99px; background:#ef4444; color:white; font-size:.66rem; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .cp2-empty { text-align:center; padding:2.5rem 1rem; color:#9094a8; font-size:.8rem; }
  .cp2-thread { display:flex; flex-direction:column; min-height:0; }
  .cp2-thread-header { padding:14px 18px; border-bottom:1px solid #edf0f9; display:flex; align-items:center; gap:10px; flex-shrink:0; }
  .cp2-thread-name { font-size:.9rem; font-weight:700; color:#0f1123; }
  .cp2-thread-sub { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .cp2-messages { flex:1; min-height:0; overflow-y:auto; padding:16px 18px; display:flex; flex-direction:column; gap:8px; }
  .cp2-msg-row { display:flex; }
  .cp2-msg-row.mine { justify-content:flex-end; }
  .cp2-bubble { max-width:70%; padding:9px 13px; border-radius:14px; font-size:.83rem; line-height:1.5; }
  .cp2-msg-row.mine .cp2-bubble { background:${accent}; color:white; border-bottom-right-radius:4px; }
  .cp2-msg-row:not(.mine) .cp2-bubble { background:#f0f2f7; color:#1a1d2e; border-bottom-left-radius:4px; }
  .cp2-msg-time { font-size:.62rem; margin-top:4px; opacity:.7; }
  .cp2-input-row { display:flex; gap:8px; padding:12px 14px; border-top:1px solid #edf0f9; flex-shrink:0; }
  .cp2-input { flex:1; font-family:'Sora',sans-serif; font-size:.83rem; padding:10px 13px; border:1.5px solid #e0e4f0; border-radius:20px; outline:none; background:#f8f9fc; }
  .cp2-input:focus { border-color:${accent}; background:white; }
  .cp2-send { width:38px; height:38px; border-radius:50%; background:${accent}; border:none; color:white; display:flex; align-items:center; justify-content:center; cursor:pointer; flex-shrink:0; }
  .cp2-send svg { width:16px; height:16px; }
  .cp2-send:disabled { opacity:.5; cursor:not-allowed; }
  .cp2-placeholder { flex:1; display:flex; align-items:center; justify-content:center; flex-direction:column; gap:8px; color:#b0b5cc; }
  .cp2-placeholder svg { width:40px; height:40px; }
  .cp2-back { display:none; }
  @media (max-width:760px) { .cp2-back { display:inline-flex; background:none; border:none; cursor:pointer; color:${accent}; font-size:.78rem; font-weight:600; margin-right:4px; } }
`;

const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';

export function createChatPage({ myRole, contactLabel, accent }) {
  return function ChatPage() {
    const [contacts, setContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(true);
    const [search, setSearch] = useState('');
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const lastIdRef = useRef(0);
    const endRef = useRef(null);
    const activeRef = useRef(null);

    const loadContacts = () => {
      api.get(`/chat/contacts`).then(r => setContacts(r.data || [])).catch(() => toast.error('Failed to load contacts')).finally(() => setLoadingContacts(false));
    };
    useEffect(() => { loadContacts(); }, []);

    const loadMessages = async (threadId, initial) => {
      try {
        const qs = initial ? '' : `?afterId=${lastIdRef.current}`;
        const { data } = await api.get(`/chat/threads/${threadId}/messages${qs}`);
        if (data.length) {
          lastIdRef.current = data[data.length - 1]._id;
          setMessages(prev => initial ? data : [...prev, ...data]);
          if (activeRef.current === threadId) {
            api.patch(`/chat/threads/${threadId}/read`).then(() => { loadContacts(); window.dispatchEvent(new Event('chat-unread-changed')); }).catch(() => {});
          }
        }
      } catch { /* silent — next poll tick retries */ }
    };

    const openContact = async (contact) => {
      setMessages([]);
      lastIdRef.current = 0;
      let threadId = contact.threadId;
      if (!threadId) {
        try {
          const { data } = await api.post(`/chat/threads`, { contactUserId: contact.contactUserId });
          threadId = data.threadId;
        } catch (err) { toast.error(err.response?.data?.message || 'Failed to open conversation'); return; }
      }
      activeRef.current = threadId;
      setActive({ threadId, contactUserId: contact.contactUserId, name: contact.name, childName: contact.childName });
      await loadMessages(threadId, true);
      api.patch(`/chat/threads/${threadId}/read`).then(() => { loadContacts(); window.dispatchEvent(new Event('chat-unread-changed')); }).catch(() => {});
    };

    useEffect(() => {
      if (!active) return;
      const id = setInterval(() => loadMessages(active.threadId, false), 5000);
      return () => clearInterval(id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active?.threadId]);

    useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

    const send = async () => {
      const body = input.trim();
      if (!body || !active) return;
      setSending(true);
      try {
        const { data } = await api.post(`/chat/threads/${active.threadId}/messages`, { body });
        setMessages(prev => [...prev, data]);
        lastIdRef.current = data._id;
        setInput('');
        loadContacts();
      } catch (err) { toast.error(err.response?.data?.message || 'Failed to send'); }
      finally { setSending(false); }
    };

    const filtered = contacts.filter(c => !search.trim() || c.name.toLowerCase().includes(search.toLowerCase()));

    return (
      <div className="cp2"><style>{css(accent)}</style>
        <div className="cp2-header">
          <div className="cp2-title">Messages</div>
          <div className="cp2-sub">Chat directly with {contactLabel}</div>
        </div>

        <div className="cp2-body">
          <div className={`cp2-list${active ? ' hide-mobile' : ''}`}>
            <div className="cp2-search">
              <div className="cp2-search-box">
                <MagnifyingGlassIcon />
                <input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="cp2-contacts">
              {loadingContacts ? <div className="cp2-empty">Loading…</div>
              : filtered.length === 0 ? <div className="cp2-empty">No {contactLabel} to message yet.</div>
              : filtered.map(c => (
                <div key={c.contactUserId} className={`cp2-contact${active?.contactUserId === c.contactUserId ? ' active' : ''}`} onClick={() => openContact(c)}>
                  <div className="cp2-avatar">{initials(c.name)}</div>
                  <div className="cp2-contact-info">
                    <div className="cp2-contact-name">{c.name}</div>
                    <div className="cp2-contact-sub">{c.childName}</div>
                  </div>
                  {c.unreadCount > 0 && <div className="cp2-unread">{c.unreadCount > 9 ? '9+' : c.unreadCount}</div>}
                </div>
              ))}
            </div>
          </div>

          <div className={`cp2-thread${!active ? ' hide-mobile' : ''}`}>
            {!active ? (
              <div className="cp2-placeholder">
                <ChatBubbleLeftRightIcon />
                <div>Select a conversation to start messaging</div>
              </div>
            ) : (
              <>
                <div className="cp2-thread-header">
                  <button className="cp2-back" onClick={() => setActive(null)}>‹ Back</button>
                  <div>
                    <div className="cp2-thread-name">{active.name}</div>
                    {active.childName && <div className="cp2-thread-sub">{active.childName}</div>}
                  </div>
                </div>
                <div className="cp2-messages">
                  {messages.map(m => (
                    <div key={m._id} className={`cp2-msg-row${m.senderRole === myRole ? ' mine' : ''}`}>
                      <div className="cp2-bubble">
                        {m.body}
                        <div className="cp2-msg-time">{fmtTime(m.createdAt)}</div>
                      </div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
                <div className="cp2-input-row">
                  <input
                    className="cp2-input" placeholder="Type a message…" value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                  />
                  <button className="cp2-send" onClick={send} disabled={sending || !input.trim()}><PaperAirplaneIcon /></button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };
}
